import { useCallback, useEffect, useRef, useState } from "react";
import {
  productRestrictionsService,
  type ProductRestrictionDetails,
  type ProductRestrictionProductType,
} from "../services/productRestrictionsService";

export interface ProductRestrictionTarget {
  productType: ProductRestrictionProductType;
  productId: number;
}

export type ProductRestrictionRequestState =
  | { status: "loading" }
  | { status: "success"; details: ProductRestrictionDetails }
  | { status: "not-found" }
  | { status: "error" };

export interface UseProductRestrictionDetailsResult {
  detailsByProduct: Record<string, ProductRestrictionRequestState>;
  retry: () => void;
}

export function getProductRestrictionKey(
  product: ProductRestrictionTarget
): string {
  return `${product.productType}:${product.productId}`;
}

function getErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  if ("status" in error && typeof error.status === "number") return error.status;
  if (
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "status" in error.response &&
    typeof error.response.status === "number"
  ) {
    return error.response.status;
  }
  return undefined;
}

function uniqueTargets(
  targets: readonly ProductRestrictionTarget[]
): ProductRestrictionTarget[] {
  const byKey = new Map<string, ProductRestrictionTarget>();
  targets.forEach((target) => byKey.set(getProductRestrictionKey(target), target));
  return Array.from(byKey.values());
}

export function useProductRestrictionDetails(
  targets: readonly ProductRestrictionTarget[],
  enabled: boolean
): UseProductRestrictionDetailsResult {
  const [detailsByProduct, setDetailsByProduct] = useState<
    Record<string, ProductRestrictionRequestState>
  >({});
  const [retryToken, setRetryToken] = useState(0);
  const requestIdRef = useRef(0);
  const targetsRef = useRef(targets);
  targetsRef.current = targets;

  const targetSignature = uniqueTargets(targets)
    .map(getProductRestrictionKey)
    .sort()
    .join("|");

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    if (!enabled || targetSignature.length === 0) {
      setDetailsByProduct({});
      return;
    }

    const requestTargets = uniqueTargets(targetsRef.current);
    const controller = new AbortController();
    setDetailsByProduct(
      Object.fromEntries(
        requestTargets.map((target) => [
          getProductRestrictionKey(target),
          { status: "loading" } as ProductRestrictionRequestState,
        ])
      )
    );

    void Promise.all(
      requestTargets.map(async (target) => {
        const key = getProductRestrictionKey(target);
        try {
          const details = await productRestrictionsService.getProductRestrictions(
            target.productType,
            target.productId,
            controller.signal
          );
          return [key, { status: "success", details }] as const;
        } catch (error) {
          if (controller.signal.aborted) return null;
          const state: ProductRestrictionRequestState =
            getErrorStatus(error) === 404
              ? { status: "not-found" }
              : { status: "error" };
          return [key, state] as const;
        }
      })
    ).then((results) => {
      if (controller.signal.aborted || requestId !== requestIdRef.current) return;
      setDetailsByProduct(
        Object.fromEntries(
          results.filter(
            (result): result is NonNullable<typeof result> => result !== null
          )
        )
      );
    });

    return () => controller.abort();
  }, [enabled, retryToken, targetSignature]);

  const retry = useCallback(() => setRetryToken((value) => value + 1), []);

  return { detailsByProduct, retry };
}
